import Bugsnag from "@bugsnag/js";
import { onValue, ref } from "firebase/database";
import { isEmpty } from "lodash-es";

import axiosInstance from "../../axiosInstance";
import URL from "../../urls";
import { PRO_SKU_LIST, apiURL } from "../../variable";
import { trackEvent } from "../../helperFunctions";
import { FIREDB } from "../../firebaseInstance";
import {
  CLIENT_APP_DATA_LOADING,
  SET_CLIENT_PROFILE,
  FETCH_CLIENT_LIST,
  RESET_CLIENTS_LIST,
  CLIENT_LIST_LOADING,
  CLIENT_PROFILE_LOADING,
  GET_CLIENT_TOOLS_SUCCESS,
  CLEAR_CLIENT_PROFILE,
  ADD_TOAST,
  ASSIGN_TOOL_SUCCESS,
  VIEW_TOOL_SUCCESS,
  SEND_REMINDER_SUCCESS,
  CLEAR_TIMELINE_TOOLS_SUCCESS,
  ALL_TOOL_SUCCESS,
  GET_TOOL_TYPE_SUCCESS,
  CUSTOM_JOURNAL_QUESTION_SUCCESS,
  SAVE_NEW_TEMPLATE_SUCCESS,
  SHORTCUT_LISTING_SUCCESS,
  SHORTCUT_SEARCH_SUCCESS,
  ASSIGN_ASSESSMENT_SUCCESS,
  UNASSIGN_ASSESSMENT_SUCCESS,
  PRO_ASSESSMENT_SUCCESS,
  COMPLETED_ASSESSMENTS_SUCCESS,
  ASSIGNED_ASSESSMENTS_SUCCESS,
  UNASSIGNED_ASSESSMENTS_SUCCESS,
  ASSESSMENT_DETAIL_SUCCESS,
  ASSESSMENT_RESULT_SUCCESS,
  ASSESSMENT_FOR_PSYCHIATRIST_SUCCESS,
  SEND_ASSESSMENT_REMINDER_SUCCESS,
  GET_TIMELINE_TOOLS_SUCCESS,
  FIRST_SESSION,
  CLEAR_VIEW_TOOL,
  LOAD_SESSION_ON_CLIENTS,
  CHAT_CLIENT_LIST_LOADING,
  CHAT_CLIENT_LIST,
  SET_CLIENT_CHAT,
  UPDATE_CLIENT_CHAT_ROOM_UNREAD_COUNT,
  SET_TREATMENT_PLAN_DISABLED_STATUS,
  SET_TREATMENT_PLAN_SEND_STATUS,
  SET_VERIFYING_NSP,
} from "./types";

export const getClientList = payload => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: CLIENT_LIST_LOADING, payload: true });
      const { isSupervisionMode, superviseeId, requestConfig } = payload;
      const clientListURL = isSupervisionMode
        ? `${apiURL}/supervisors/providers/${superviseeId}/clients`
        : `${URL.clientList}/list_v2`;
      const response = await axiosInstance.get(clientListURL, requestConfig);
      dispatch({
        type: FETCH_CLIENT_LIST,
        payload: {
          data: response.data.data,
          totalPages: response.data.total_page,
        },
      });
      dispatch({ type: CLIENT_LIST_LOADING, payload: false });
    } catch (error) {
      if (error?.message === "canceled") return; // required for cancelled requests
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Clients list could not be fetched. Please try again.",
          appearance: "error",
          autoDismiss: false,
        },
      });
      dispatch({ type: CLIENT_LIST_LOADING, payload: false });
    }
  };
};

export const getChatClientList = params => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: CHAT_CLIENT_LIST_LOADING, payload: true });
      const response = await axiosInstance.get(URL.chatClientList);
      dispatch({
        type: CHAT_CLIENT_LIST,
        payload: {
          data: response.data.data,
        },
      });
    } catch (error) {
      dispatch({ type: CHAT_CLIENT_LIST_LOADING, payload: false });
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Clients list could not be fetched. Please try again.",
          appearance: "error",
          autoDismiss: false,
        },
      });
    }
  };
};

export const updateClientChatRoomUnreadCount = (roomId, unreadCount) => ({
  type: UPDATE_CLIENT_CHAT_ROOM_UNREAD_COUNT,
  payload: {
    roomId: Number(roomId),
    unreadCount: Number(unreadCount) || 0,
  },
});

export const resetClientList = () => dispatch => {
  dispatch({ type: RESET_CLIENTS_LIST });
};

export const getClientAppData = user => {
  return dispatch => {
    // dispatch({ type: CLEAR_CLIENT_PROFILE });
    dispatch({ type: CLIENT_APP_DATA_LOADING, payload: true });
    dispatch({ type: SET_CLIENT_PROFILE, payload: user });
    if (
      !isEmpty(user?.subscription) &&
      PRO_SKU_LIST.includes(user?.subscription?.plan) &&
      new Date(user?.subscription?.end_date) > new Date()
    ) {
      axiosInstance
        .get(`${URL.clientAppData}/${user.firebaseid}`)
        .then(response => {
          dispatch({
            type: SET_CLIENT_PROFILE,
            payload: {
              ...response.data,
              clientDataFetchTime: new Date().getTime(),
            },
          });
          dispatch({ type: CLIENT_APP_DATA_LOADING, payload: false });
        })
        .catch(error => {
          console.log("error", error.response);
          dispatch({
            type: ADD_TOAST,
            payload: {
              msg: `${user.firstname}'s app data could not be fetched.`,
              appearance: "error",
              autoDismiss: false,
            },
          });
        });
    }
  };
};

export const getClientFeedbackData = firebaseid => {
  return dispatch => {
    dispatch({ type: CLIENT_APP_DATA_LOADING, payload: true });
    axiosInstance
      .get(`${URL.contentFeedback}/${firebaseid}`)
      .then(response =>
        dispatch({
          type: SET_CLIENT_PROFILE,
          payload: { ...response.data },
        })
      )
      .catch(error => {
        console.log("error", error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `Content feedback could not be fetched. Responses indicated may be incorrect.`,
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const getClientCases = clientID => {
  if (!clientID) return;
  return dispatch => {
    axiosInstance
      .get(`${URL.listCases}/${clientID}`)
      .then(response =>
        dispatch({ type: SET_CLIENT_PROFILE, payload: response.data })
      )
      .catch(error => {
        console.log("error", error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Cases assigned to this client could not be fetched.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const getClientTherapistGoals = firebaseid => {
  return dispatch => {
    axiosInstance
      .get(`${URL.therapistGoals}/${firebaseid}`)
      .then(response =>
        dispatch({ type: SET_CLIENT_PROFILE, payload: response.data })
      )
      .catch(error => {
        console.log("error", error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Therapist assigned goals could not be fetched.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

/**
 * Fetch client profile data and update store
 *
 * For normal mode
 * @param {string} clientUuid
 *
 * For supervision
 * @param {boolean} isSupervisionMode
 * @param {number} superviseeId
 * @param {number} clientId
 *
 * @return {Object} client profile
 */
export const getClientDetailsNew = ({
  clientUuid = null,
  isSupervisionMode = false,
  superviseeId = null,
  clientId = null,
  userFirebaseId,
} = {}) => {
  return async dispatch => {
    try {
      if (isSupervisionMode && (!superviseeId || !clientId)) return;
      const isValidUuid =
        clientUuid &&
        String(clientUuid).trim() !== "" &&
        String(clientUuid) !== "undefined";
      if (!isSupervisionMode && !isValidUuid) return;
      const url = isSupervisionMode
        ? `${apiURL}/supervisors/providers/${superviseeId}/clients/${clientId}`
        : `${URL.getClientDetails}/${clientUuid}`;

      dispatch({ type: CLIENT_PROFILE_LOADING, payload: true });
      const res = await axiosInstance.get(url, {
        params: {
          v2_version: "yes",
        },
      });
      const clientData = res?.data?.data || {};
      dispatch({
        type: SET_CLIENT_PROFILE,
        payload: clientData,
      });
      dispatch(getClientChat(userFirebaseId, clientData?.firebaseid));
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Client profile could not be fetched. Please try again.",
          appearance: "error",
          autoDismiss: true,
        },
      });
    } finally {
      dispatch({ type: CLIENT_PROFILE_LOADING, payload: false });
    }
  };
};

export const getClientChat =
  (userFirebaseId, clientFirebaseId) => async dispatch => {
    if (!userFirebaseId || !clientFirebaseId) return;
    try {
      dispatch({
        type: SET_CLIENT_CHAT,
        payload: { loading: true, data: null, error: "" },
      });
      const dbRef = ref(
        FIREDB,
        `user_friend_map/${userFirebaseId}/${clientFirebaseId}`
      );
      onValue(dbRef, snapshot => {
        dispatch({
          type: SET_CLIENT_CHAT,
          payload: { loading: false, data: snapshot.val(), error: "" },
        });
      });
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: SET_CLIENT_CHAT,
        payload: { loading: false, data: null, error: error?.message },
      });
    }
  };

export const getClientGeneralTools = ({
  id,
  toolId = "",
  status = "",
  pageNumber = 1,
  isGeneric,
  condition,
}) => {
  return dispatch => {
    axiosInstance
      .get(`${URL.customers}/${id}/tools`, {
        params: {
          is_generic: isGeneric,
          tool_type_ids: toolId,
          status_id: status,
          page: pageNumber,
          limit: 10,
          template_types: condition,
        },
      })
      .then(response =>
        dispatch({ type: GET_CLIENT_TOOLS_SUCCESS, payload: response.data })
      )
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `failed loading the general tool list for client ${id}`,
            appearance: "error",
          },
        });
      });
  };
};

export const getClientTimelineTools = (id, pageNumber = 1) => {
  return dispatch => {
    axiosInstance
      .get(`${URL.customers}/${id}/tools/timeline?page=${pageNumber}&limit=10`)
      .then(response =>
        dispatch({ type: GET_TIMELINE_TOOLS_SUCCESS, payload: response.data })
      )
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `failed loading the timeline tool list for client ${id}`,
            appearance: "error",
          },
        });
      });
  };
};

export const assignTool = (id, payload, analyticPayload) => {
  return dispatch => {
    axiosInstance
      .post(`${URL.customers}/${id}/tools`, payload)
      .then(response => {
        dispatch({ type: ASSIGN_TOOL_SUCCESS, payload: response.data });
        trackEvent(analyticPayload);
      })
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed to assign tool ",
            appearance: "error",
          },
        });
      });
  };
};

export const getToolMediaType = (toolId, customerUuid) => {
  return dispatch => {
    axiosInstance
      .get(`${URL.getToolTypeOfMedia}/${toolId}/tool_uploads`, {
        params: {
          page: 1,
          limit: 100,
          customer_uuid: customerUuid,
        },
      })
      .then(response =>
        dispatch({ type: GET_TOOL_TYPE_SUCCESS, payload: response.data })
      )
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed loading tool media type",
            appearance: "error",
          },
        });
      });
  };
};

export const getAllTools = () => {
  return dispatch => {
    axiosInstance
      .get(`${URL.getToolTypeOfMedia}?page=1&limit=10`)
      .then(response =>
        dispatch({
          type: ALL_TOOL_SUCCESS,
          payload: response.data.data,
        })
      )
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed loading the all tools list ",
            appearance: "error",
          },
        });
      });
  };
};

export const sendReminder = (clientId, toolId, payload) => {
  return dispatch => {
    axiosInstance
      .put(`${URL.customers}/${clientId}/tools/${toolId}/remind`, payload)
      .then(response => {
        dispatch({ type: SEND_REMINDER_SUCCESS });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed to send reminder for assigned tool",
            appearance: "error",
          },
        });
        console.log(err);
      });
  };
};
export const viewTool = ({ clientId, toolId }) => {
  return dispatch => {
    axiosInstance
      .get(`${URL.customers}/${clientId}/tools/${toolId}`)
      .then(response => {
        dispatch({ type: VIEW_TOOL_SUCCESS, payload: response });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed to load tool details",
            appearance: "error",
          },
        });
        console.log(err);
      });
  };
};

export const clearViewTool = () => {
  return dispatch => {
    dispatch({ type: CLEAR_VIEW_TOOL });
  };
};

export const saveNewTemplate = ({ id, payload }) => {
  return dispatch => {
    axiosInstance
      .post(`${URL.saveNewTemplate}/${id}/save_as_template`, payload)
      .then(response => {
        dispatch({ type: SAVE_NEW_TEMPLATE_SUCCESS });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "the template was not able to save ,please choose a different title ",
            appearance: "error",
          },
        });
        console.log(err);
      });
  };
};

export const CustomJournalQuestions = ({ question }) => {
  return dispatch => {
    axiosInstance
      .get(`${URL.customGNToolsQuestion}?query=${question}`)
      .then(response => {
        dispatch({ type: CUSTOM_JOURNAL_QUESTION_SUCCESS, payload: response });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed to load custom tool questions",
            appearance: "error",
          },
        });
        console.log(err);
      });
  };
};

export const fetchAssessmentForPsychiatrist = uuid => {
  return dispatch => {
    axiosInstance
      .get(`${URL.psychiatristCompletedAssessments}/${uuid}`)
      .then(response => {
        dispatch({
          type: ASSESSMENT_FOR_PSYCHIATRIST_SUCCESS,
          payload: {
            completedAssessments: response.data.assessments,
            initial_assessment_taken: response.data.initial_assessment_taken,
            initial_assessment:
              response.data.initial_assessment_taken === "completed"
                ? response.data.initial_assessment_result
                : {},
          },
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "failed to fetch Psychiatrist assessments",
            appearance: "error",
          },
        });
      });
  };
};

export const fetchAssessments = uuid => {
  return dispatch => {
    //pro assessments
    axiosInstance
      .get(`${URL.proassessment}/${uuid}`)
      .then(res =>
        dispatch({
          type: PRO_ASSESSMENT_SUCCESS,
          payload: res.data,
        })
      )
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch Pro Assessment",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });

    //completed assessments
    axiosInstance
      .get(`${URL.completedAssessments}/${uuid}`)
      .then(res =>
        dispatch({
          type: COMPLETED_ASSESSMENTS_SUCCESS,
          payload: {
            completedAssessments: res.data.assessments,
            initial_assessment_taken: res.data.initial_assessment_taken,
            initial_assessment:
              res.data.initial_assessment_taken === "completed"
                ? res.data.initial_assessment_result
                : {},
          },
        })
      )
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch completed assessments",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });

    //assigned assessments
    axiosInstance
      .get(`${URL.assignedAssessments}/${uuid}`)
      .then(res =>
        dispatch({
          type: ASSIGNED_ASSESSMENTS_SUCCESS,
          payload: res.data.assessments,
        })
      )
      .catch(err =>
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch assigned assessments",
            appearance: "error",
            autoDismiss: false,
          },
        })
      );

    //unassigned assessments
    axiosInstance
      .get(`${URL.unassignedAssessments}/${uuid}`)
      .then(res =>
        dispatch({
          type: UNASSIGNED_ASSESSMENTS_SUCCESS,
          payload: res.data.assessments,
        })
      )
      .catch(err =>
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch unassigned assessments",
            appearance: "error",
            autoDismiss: false,
          },
        })
      );
  };
};

export const assignAssessment = (payload, uuid, analyticPayload) => {
  return dispatch => {
    axiosInstance
      .put(URL.assignAssessment, payload)
      .then(response => {
        dispatch({ type: ASSIGN_ASSESSMENT_SUCCESS });
        dispatch(fetchAssessments(uuid));
        trackEvent(analyticPayload);
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Assessment assignment failed",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const unassignAssessment = (payload, uuid) => {
  return dispatch => {
    axiosInstance
      .put(URL.unassignAssessment, payload)
      .then(response => {
        dispatch({ type: UNASSIGN_ASSESSMENT_SUCCESS });
        dispatch(fetchAssessments(uuid));
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Withdraw assessment failed",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const getAssessmentDetail = assessment => {
  return dispatch => {
    axiosInstance
      .get(`${URL.assessmentDetail}/${assessment.slug}`)
      .then(response => {
        dispatch({
          type: ASSESSMENT_DETAIL_SUCCESS,
          payload: response.data.assessment,
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to fetch assessment detail",
            appearance: "error",
          },
        });
      });
  };
};

export const getAssessmentResults = payload => {
  return dispatch => {
    axiosInstance
      .post(`${URL.assessmentResult}`, payload)
      .then(response => {
        dispatch({
          type: ASSESSMENT_RESULT_SUCCESS,
          payload: response.data.assessment_answers,
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to fetch assessment results",
            appearance: "error",
          },
        });
      });
  };
};

export const sendAssessmentReminder = payload => {
  return dispatch => {
    axiosInstance
      .post(URL.setAssessmentReminder, payload)
      .then(response => dispatch({ type: SEND_ASSESSMENT_REMINDER_SUCCESS }))
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to send reminder for assigned assessment",
            appearance: "error",
          },
        });
      });
  };
};

export const resetTimelineTools = () => {
  return dispatch => {
    dispatch({ type: CLEAR_TIMELINE_TOOLS_SUCCESS });
  };
};

export const getChatShortcuts = (pageNumber, customerUuid, limit) => {
  return dispatch => {
    axiosInstance
      .get(URL.getChatShortcuts, {
        params: {
          page: pageNumber,
          limit: limit ? limit : 15,
          customer_uuid: customerUuid,
        },
      })
      .then(response => {
        dispatch({
          type: SHORTCUT_LISTING_SUCCESS,
          payload: response,
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to load chat shortcuts",
            appearance: "error",
          },
        });
      });
  };
};

export const searchChatShortcut = (shortcutName, customerUuid) => {
  return dispatch => {
    axiosInstance
      .get(URL.searchChatShortcut, {
        params: {
          query: shortcutName,
          customer_uuid: customerUuid,
        },
      })
      .then(response => {
        dispatch({
          type: SHORTCUT_SEARCH_SUCCESS,
          payload: response,
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to load shortcut result for your search",
            appearance: "error",
          },
        });
      });
  };
};

export const createChatShortcut = payload => {
  return dispatch => {
    axiosInstance
      .post(URL.getChatShortcuts, payload)
      .then(() =>
        dispatch({
          type: ADD_TOAST,
          payload: { msg: "The new shortcut has been saved." },
        })
      )
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to create the shortcut",
            appearance: "error",
          },
        });
      });
  };
};

export const updateChatShortcuts = (payload, id) => {
  return dispatch => {
    axiosInstance
      .put(`${URL.getChatShortcuts}/${id}`, payload)
      .then(() => {
        dispatch({
          type: ADD_TOAST,
          payload: { msg: "The shortcut has been updated." },
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Failed to update the shortcut",
            appearance: "error",
          },
        });
      });
  };
};

export const removeChatShortcuts = id => {
  return dispatch => {
    axiosInstance.delete(`${URL.getChatShortcuts}/${id}`).catch(err => {
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Failed to remove the shortcut",
          appearance: "error",
        },
      });
    });
  };
};

export const updateFirstSession = completedSessions => ({
  type: FIRST_SESSION,
  payload: completedSessions,
});

export const loadSessionOnClientProfile = loadSession => ({
  type: LOAD_SESSION_ON_CLIENTS,
  payload: loadSession,
});

export const clearClientData = () => ({
  type: CLEAR_CLIENT_PROFILE,
});

export const setTreatmentPlanDisabledStatus = disableOption => ({
  type: SET_TREATMENT_PLAN_DISABLED_STATUS,
  payload: disableOption,
});

export const setTreatmentPlanSendStatus = isSaving => ({
  type: SET_TREATMENT_PLAN_SEND_STATUS,
  payload: isSaving,
});

export const getTreatmentPlanCreationStatus = clientId => async dispatch => {
  try {
    if (!clientId) return;
    const response = await axiosInstance.get(
      `${apiURL}/providers/customers/${clientId}/treatment_plans/treatment_plan_creation_status`
    );
    const disable_treatment_plan_option =
      !!response?.data?.data?.disable_treatment_plan_option;
    dispatch(setTreatmentPlanDisabledStatus(disable_treatment_plan_option));
  } catch (error) {
    Bugsnag.notify(error);
  }
};

export const setVerifyingNsp = isVerifying => ({
  type: SET_VERIFYING_NSP,
  payload: isVerifying,
});
